import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { filter, Subscription, take } from 'rxjs';
import { appConstants } from '../../../../../shared/constants/app-constants';
import { IUser } from '../../../../../shared/models/user';
import { AuthService } from '../../../../../shared/services/auth.service';
import { getUpdateTimeStampWording } from '../../../../../shared/utilities/date-utility';
import { UserUtility } from '../../../../../shared/utilities/user-utility';
import { ISdBallotVote } from '../../../shared/models/sd-ballot-vote';
import { IProjectDetails } from '../../../shared/models/project-details';
import { ChartjsService } from '../../../shared/services/chartjs.service';
import { orderBy } from 'lodash';
import { SdVoteId, sdVoteOption } from '../../../../../shared/constants/sd-ballot.constants';
import { IDonutChartItem } from '../../../../controls/donut-chart/donut-chart.component';
import { SdBallotService } from '../../../shared/services/sd-ballot.service';


@Component({
    selector: 'app-project-details-overview-ballot',
    templateUrl: './project-details-overview-ballot.component.html',
    styleUrl: './project-details-overview-ballot.component.scss',
    standalone: false
})
export class ProjectDetailsOverviewBallotComponent implements OnInit, OnDestroy, OnChanges {

  positiveColour: string = '#6589F7';
  negativeColour: string = '#BDCBFC';
  notVotedColour: string = '#ABABAB';

  @Input() projectDetails: IProjectDetails | null = null;
  @ViewChild('chartBallot') canvas: ElementRef<HTMLCanvasElement> = {} as ElementRef<HTMLCanvasElement>;

  currentUser: IUser | null = null;
  votes: ISdBallotVote[] = [];

  authorized: boolean = false;
  loading: boolean = true;
  subscriptions: Subscription = new Subscription();
  unableLoadingBallot: boolean = false;

  commentingStartDate: Date | null = null;
  commentingEndDate: Date | null = null;
  commentPeriodStatus: string = '';
  sdBallotStatus: string = '';

  positiveVotesCount: number = 0;
  negativeVotesCount: number = 0;
  notVotedCount: number = 0;
  latestVoteDateWording: string = '';
  showConnectViewButton: boolean = false;
  NA: string = appConstants.sdBallots.detailStatus.NA;
  disableViewBallot: boolean = false;

  chartData: IDonutChartItem[] = [];

  constructor(
    private chartjsService: ChartjsService,
    private _authService: AuthService,
    private _sdBallotService: SdBallotService,
  ) {
  }

  ngOnInit(): void {
      const userSubscription = this._authService.currentUserSubject
    .pipe(
        filter(user => !!user),
        take(1)
      )
      .subscribe(user => {
        if (user) {
          this.currentUser = user;

          this.initialise();
        }
      });
      this.subscriptions.add(userSubscription);
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectDetails'] && changes['projectDetails'].currentValue && !changes['projectDetails'].firstChange) {
      this.projectDetails = changes['projectDetails'].currentValue;

      this.initialise();

    }
  }

  initialise(): void {
    if (!this.projectDetails || !this.currentUser) {
      return;
    }

    this.authorized = UserUtility.canViewSdBallotDetails(this.currentUser, this.projectDetails.committeeCode);
    if (!this.authorized) {
      this.loading = false;
      return;
    }
    if(this.projectDetails && this.projectDetails.ballotStatusId) {
      this.sdBallotStatus = this.projectDetails.ballotStatusId;
    }
    this.getBallotVotes();
    this.initialiseViewButtons();
  }

  getBallotVotes() {
    if (!this.projectDetails || !this.currentUser) {
      return;
    }

    this.loading = true;

    this._sdBallotService
    .getVotesByProjectId(this.projectDetails.projectId)
    .subscribe({
      next: (response) => {
        this.votes = response.success ? response.content : [];
        const voteCounts = this.generateVoteCounts(this.votes);
        this.generateDonutChart(voteCounts);

        const latestVote = orderBy(this.votes, ['lastUpdated'], ['desc'])[0];
        if (latestVote && latestVote.voteId !== appConstants.sdBallots.voteOptions.notCast && latestVote.lastUpdated) {
          this.latestVoteDateWording = getUpdateTimeStampWording(latestVote.lastUpdated)
        }

        this.loading = false;
      },
      error: _ => {
        this.loading = false;
        this.unableLoadingBallot = true;
      },
    });
  }

  generateVoteCounts(votes: ISdBallotVote[]): { [key in SdVoteId]: number } {
    const voteCounts: { [key in SdVoteId]: number } = {
      [SdVoteId.VT_AGREE]: 0,
      [SdVoteId.VT_DISAGREE]: 0,
      [SdVoteId.VT_NONE]: 0
    };

    const eligibleVotes = votes.filter(x => x.isEligible);

    eligibleVotes.forEach(vote => {
      if (vote.voteId in voteCounts) {
        voteCounts[vote.voteId as SdVoteId]++;
      }
    });

    return voteCounts;
  }

  generateDonutChart(voteCounts: { [key in SdVoteId]: number }): void {

    this.chartData = Object.values(SdVoteId).map((voteType) => ({
      label: sdVoteOption[voteType].text,
      value: voteCounts[voteType] || 0,
      weight: 0,
      color: sdVoteOption[voteType].color,
      icon: sdVoteOption[voteType].icon,
    }));
  }

  initialiseViewButtons(): void {
    if (!this.projectDetails || !this.currentUser) {
      return;
    }

    const isDraftOrCommentPhase = [
      appConstants.publicationStatus.drafting,
      appConstants.publicationStatus.commenting,
      appConstants.publicationStatus.commentingCompleted
    ].indexOf(this.projectDetails.phaseId) !== -1;

    if(isDraftOrCommentPhase || this.commentingAndBallotingPhaseCheck()){
      this.showConnectViewButton = true;
      this.disableViewBallot = true;
      return;
    }

    const isPmOrAbove = UserUtility.userIsPmOrAbove(this.currentUser);
    const isPartOfCommittee = !UserUtility.isMemberOfPublicOnly(this.currentUser)
                              && UserUtility.userIsOnCommittee(this.currentUser, this.projectDetails.committeeCode);

    const isAuthorized = isPmOrAbove || isPartOfCommittee;
    const validBallotStatus = isPmOrAbove
      || (isPartOfCommittee && this.projectDetails.ballotStatusId !== appConstants.sdBallots.status.notOpen);

    this.showConnectViewButton = isAuthorized && validBallotStatus && this.projectDetails.existInConnect;
    this.disableViewBallot = isPartOfCommittee && (this.projectDetails.ballotStatusId == appConstants.sdBallots.status.notOpen);
    if(this.disableViewBallot) {
      this.showConnectViewButton = true;
    }
  }


  commentingAndBallotingPhaseCheck(){
    return this.projectDetails?.phaseId == appConstants.publicationStatus.commentingAndBalloting && !this.projectDetails?.ballotStatusId;
  }
}
